import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import NoImageIcon from '../../resources/svg/no_image.svg';
import css from './OGTagsPreview.css';

function OGTagsPreview({
    label = '',
    // Dynamic props from form values
    ogTitle = '',
    ogDescription = '',
    ogImageUrl = '',
    subDomain = '',
}) {
    // Image loading states
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    // Image preloading with file size check and timeout mechanism
    useEffect(() => {
        if (ogImageUrl) {
            setImageLoading(true);
            setImageError(false);

            const abortController = new AbortController();
            let timeout;

            const checkImageSizeAndLoad = async () => {
                try {
                    // Try to check file size using HEAD request
                    const headResponse = await fetch(ogImageUrl, {
                        method: 'HEAD',
                        signal: abortController.signal,
                    });

                    if (headResponse.ok) {
                        // Check content-length header for file size
                        const contentLength = headResponse.headers.get('content-length');
                        if (contentLength) {
                            const fileSizeInBytes = parseInt(contentLength, 10);
                            const fileSizeInMB = fileSizeInBytes / (1024 * 1024);

                            // Reject images larger than 8MB
                            if (fileSizeInMB > 8) {
                                setImageLoading(false);
                                setImageError(true);
                                return;
                            }
                        }
                    }
                } catch (error) {
                    console.error('Image size check failed : ', error);
                }

                // Proceed with image loading regardless of size check result
                const img = new Image();

                // Set up timeout to prevent infinite loading (10 seconds)
                timeout = setTimeout(() => {
                    setImageLoading(false);
                    setImageError(true);
                }, 10000);

                img.onload = () => {
                    if (timeout) clearTimeout(timeout);
                    setImageLoading(false);
                    setImageError(false);
                };

                img.onerror = () => {
                    if (timeout) clearTimeout(timeout);
                    setImageLoading(false);
                    setImageError(true);
                };

                // Start preloading
                img.src = ogImageUrl;
            };

            checkImageSizeAndLoad();

            // Cleanup function
            return () => {
                abortController.abort();
                if (timeout) clearTimeout(timeout);
            };
        } else {
            // No URL provided - will show SVG placeholder, no loading needed
            setImageLoading(false);
            setImageError(false);
        }
    }, [ogImageUrl]);

    // Loading state components
    const ImageLoadingSpinner = () => (
        <div className={css.imageLoading}>
            <div className={css.spinner} />
            <span className={css.loadingText}>Loading image...</span>
        </div>
    );

    const ImageErrorPlaceholder = () => (
        <div className={css.imageError}>
            <NoImageIcon className={css.previewImage} />
            <span className={css.errorText}>Image unavailable</span>
        </div>
    );

    return (
        <div className={css.container}>
            {label && (
                <div className={css.labelText}>
                    <span>
                        <Translate id={label} />
                    </span>
                </div>
            )}
            <div className={css.previewBox}>
                <div className={css.previewContent}>
                    <div className={css.imageContainer}>
                        {ogImageUrl ? (
                            // User provided an image URL
                            imageLoading ? (
                                <ImageLoadingSpinner />
                            ) : imageError ? (
                                <ImageErrorPlaceholder />
                            ) : (
                                <img src={ogImageUrl} alt="OG Preview" className={css.previewImage} />
                            )
                        ) : (
                            // No image URL provided - show SVG placeholder
                            <NoImageIcon className={css.previewImage} />
                        )}
                    </div>
                    <div className={css.textContent}>
                        <p className={css.previewLink}>
                            {subDomain || <Translate id="STATIC.PAGES.MANAGE_LINKS.OG_TAGS_LINK_URL_EMPTY_STATE" />}
                        </p>
                        <h3 className={css.previewTitle}>
                            {ogTitle || <Translate id="STATIC.PAGES.MANAGE_LINKS.OG_TAGS_TITLE_EMPTY_STATE" />}
                        </h3>
                        <p className={css.previewDescription}>
                            {ogDescription || (
                                <Translate id="STATIC.PAGES.MANAGE_LINKS.OG_TAGS_DESCRIPTION_EMPTY_STATE" />
                            )}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

OGTagsPreview.propTypes = {
    label: PropTypes.string,
    // Dynamic props
    ogTitle: PropTypes.string,
    ogDescription: PropTypes.string,
    ogImageUrl: PropTypes.string,
    subDomain: PropTypes.string,
};

export default OGTagsPreview;
